import moment from 'moment';
import config from '../config';

// Locale configuration interface
interface LocaleConfig {
  dateFormat: string;
  dateTimeFormat: string;
  orderDateFormat: string;
  timeFormat: string;
  shortDateFormat: string;
  monthYearFormat: string;
  keyboardDatePickerDateFormat: string;
  keyboardDatePickerDateTimeFormat: string;
  uses12Hour: boolean;
  intlLocale: string;
}

// Locale configurations
const LOCALE_CONFIGS: Record<string, LocaleConfig> = {
  'en-US': {
    dateFormat: 'MM/DD/yyyy',
    dateTimeFormat: 'MM/DD/yyyy h:mm A',
    orderDateFormat: 'MMM DD, yyyy - h:mm:ss A',
    timeFormat: 'h:mm A',
    shortDateFormat: 'MMM DD, yyyy',
    monthYearFormat: 'MMMM yyyy',
    keyboardDatePickerDateFormat: 'MM/dd/yyyy',
    keyboardDatePickerDateTimeFormat: 'MM/dd/yyyy h:mm',
    uses12Hour: true,
    intlLocale: 'en-US'
  },
  'en-GB': {
    dateFormat: 'DD/MM/yyyy',
    dateTimeFormat: 'DD/MM/yyyy HH:mm',
    orderDateFormat: 'DD MMM yyyy - h:mm:ss A',
    timeFormat: 'HH:mm',
    shortDateFormat: 'DD MMM yyyy',
    monthYearFormat: 'MMMM yyyy',
    keyboardDatePickerDateFormat: 'dd/MM/yyyy',
    keyboardDatePickerDateTimeFormat: 'dd/MM/yyyy h:mm',
    uses12Hour: false,
    intlLocale: 'en-GB'
  }
};

// Get current locale from config
const getCurrentLocale = (): string => {
  return config.locale || 'en-GB';
};

// Get locale configuration
const getLocaleConfig = (): LocaleConfig => {
  const locale = getCurrentLocale();
  return LOCALE_CONFIGS[locale] || LOCALE_CONFIGS['en-GB'];
};

// Get locale-specific formats
export const getDateFormat = (): string => getLocaleConfig().dateFormat;
export const getDateTimeFormat = (): string => getLocaleConfig().dateTimeFormat;
export const getOrderDateFormat = (): string => getLocaleConfig().orderDateFormat;
export const getTimeFormat = (): string => getLocaleConfig().timeFormat;
export const getShortDateFormat = (): string => getLocaleConfig().shortDateFormat;
export const getMonthYearFormat = (): string => getLocaleConfig().monthYearFormat;
export const getKeyboardPickerDateFormat = (): string => getLocaleConfig().keyboardDatePickerDateFormat;
export const getKeyboardPickerDateTimeFormat = (): string => getLocaleConfig().keyboardDatePickerDateTimeFormat;

// Get Intl.DateTimeFormat options for the current locale
export const getIntlDateOptions = (): Intl.DateTimeFormatOptions => {
  return {
    year: 'numeric',
    month: 'short',
    day: '2-digit'
  };
};

export const getIntlTimeOptions = (): Intl.DateTimeFormatOptions => {
  const localeConfig = getLocaleConfig();
  return {
    hour: '2-digit',
    minute: '2-digit',
    hour12: localeConfig.uses12Hour
  };
};

export const getIntlDateTimeOptions = (): Intl.DateTimeFormatOptions => {
  const localeConfig = getLocaleConfig();
  return {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: localeConfig.uses12Hour
  };
};

// Format date using moment with locale-specific format
export const formatShortDate = (date: string | Date | moment.Moment): string => {
  return moment(date).format(getShortDateFormat());
};

export const formatDate = (date: string | Date): string => {
  return moment.utc(date).local().format(getDateFormat());
}

// Format date-time using moment with locale-specific format
export const formatDateTime = (date: string | Date | moment.Moment): string => {
  return moment(date).format(getDateTimeFormat());
};

// Format time using moment with locale-specific format
export const formatTime = (date: string | Date | moment.Moment): string => {
  return moment(date).format(getTimeFormat());
};

// Format date using Intl.DateTimeFormat (more reliable for locale-specific formatting)
export const formatDateIntl = (date: string | Date): string => {
  const localeConfig = getLocaleConfig();
  return new Date(date).toLocaleDateString(localeConfig.intlLocale, getIntlDateOptions());
};

// Format time using Intl.DateTimeFormat
export const formatTimeIntl = (date: string | Date): string => {
  const localeConfig = getLocaleConfig();
  return new Date(date).toLocaleTimeString(localeConfig.intlLocale, getIntlTimeOptions());
};

// Format date-time using Intl.DateTimeFormat
export const formatDateTimeIntl = (date: string | Date): string => {
  const localeConfig = getLocaleConfig();
  return new Date(date).toLocaleString(localeConfig.intlLocale, getIntlDateTimeOptions());
};

// Format date for API calls (always use ISO format)
export const formatDateForAPI = (date: string | Date | moment.Moment): string => {
  return moment(date).format('YYYY-MM-DD');
};

// Format date-time for API calls (always use ISO format)
export const formatDateTimeForAPI = (date: string | Date | moment.Moment): string => {
  return moment(date).format('YYYY-MM-DDTHH:mm:ss');
};

// Get locale list for Intl functions
export const getLocaleList = (): string[] => {
  const localeConfig = getLocaleConfig();
  return [localeConfig.intlLocale];
};

// Legacy support - maintain existing function signatures but use locale-aware formatting
export const getOrderDate = (createdDate: string | Date): string => {
  return moment.utc(createdDate).local().format(getDateTimeFormat());
};

export const getOrderDateShort = (createdDate: string | Date): string => {
  return moment.utc(createdDate).local().format(getShortDateFormat());
};

export const getOrderDateTime = (createdDate: string | Date): string => {
  return moment.utc(createdDate).local().format(getDateTimeFormat());
};

export const getDeliveryWindow = (
  timeSlotFrom: string | Date,
  timeSlotTo: string | Date
): string => {
  const fromTime = moment.utc(timeSlotFrom).local().format(getTimeFormat());
  const toTime = moment.utc(timeSlotTo).local().format(getTimeFormat());
  return `${fromTime} - ${toTime}`;
};

export const getDeliveryTime = (time: string | Date): string => {
  return moment.utc(time).local().format(getTimeFormat());
};

export const getDeliveryDate = (date: string | Date): string => {
  return moment.utc(date).local().format(getShortDateFormat());
};

export const getLastUpdateDate = (data: any): string => {
  const lastUpdate = data.LastUpdate || data.Updated || data.Created;
  return moment.utc(lastUpdate).local().format(getDateTimeFormat());
};

// Export locale info for components that need it
export { getCurrentLocale };
export const isUSLocale = (): boolean => getCurrentLocale() === 'en-US';
export const isGBLocale = (): boolean => getCurrentLocale() === 'en-GB';
